(() => {
  'use strict';

  const MODULE_KEY = '__ctDeliveryWidgetModule';
  const ROOT_SELECTOR = '[data-ct-delivery-widget]';
  const MONTH_NAMES = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

  if (window[MODULE_KEY]) {
    window[MODULE_KEY].init(document);
    return;
  }

  const controllers = new WeakMap();
  const formatters = new Map();

  const getFormatter = (timeZone) => {
    if (!formatters.has(timeZone)) {
      formatters.set(
        timeZone,
        new Intl.DateTimeFormat('en-US', {
          timeZone,
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          second: 'numeric',
          hourCycle: 'h23',
        })
      );
    }

    return formatters.get(timeZone);
  };

  const getZonedParts = (date, timeZone) => {
    try {
      const values = {};
      getFormatter(timeZone)
        .formatToParts(date)
        .forEach(({ type, value }) => {
          if (type !== 'literal') values[type] = Number(value);
        });

      if (!values.year || !values.month || !values.day) throw new Error('Invalid zoned date');
      return values;
    } catch (error) {
      const fallback = new Date(date.toLocaleString('en-US', { timeZone }));
      return {
        year: fallback.getFullYear(),
        month: fallback.getMonth() + 1,
        day: fallback.getDate(),
        hour: fallback.getHours(),
        minute: fallback.getMinutes(),
        second: fallback.getSeconds(),
      };
    }
  };

  const formatCalendarDate = (date) => `${date.getUTCDate()} ${MONTH_NAMES[date.getUTCMonth()]}`;

  const replaceStatus = (element, before, emphasis, after = '') => {
    const strong = document.createElement('b');
    strong.textContent = emphasis;
    element.replaceChildren(document.createTextNode(before), strong, document.createTextNode(after));
  };

  const createController = (root) => {
    const boughtElement = root.querySelector('[data-ct-delivery-bought]');
    const receivedElement = root.querySelector('[data-ct-delivery-received]');
    const statusElement = root.querySelector('[data-ct-delivery-status]');
    const timeZone = root.dataset.timeZone || 'America/Asuncion';
    const configuredCutoff = Number.parseInt(root.dataset.cutoffHour || '17', 10);
    const cutoffHour = Number.isInteger(configuredCutoff) && configuredCutoff >= 0 && configuredCutoff <= 23
      ? configuredCutoff
      : 17;
    let timerId = 0;

    const update = () => {
      if (!root.isConnected || !boughtElement || !receivedElement || !statusElement) return;

      const parts = getZonedParts(new Date(), timeZone);
      const today = new Date(Date.UTC(parts.year, parts.month - 1, parts.day));
      const deliveryDate = new Date(today.getTime());
      const dayOfWeek = today.getUTCDay();
      const dailyCutoffHour = dayOfWeek === 6 ? 14 : cutoffHour;
      const isSunday = dayOfWeek === 0;
      const isAfterCutoff = parts.hour >= dailyCutoffHour;
      let deliveryStatus = '';

      if (isSunday) {
        deliveryDate.setUTCDate(deliveryDate.getUTCDate() + 1);
        deliveryStatus = 'MAÑANA';
      } else if (isAfterCutoff) {
        if (dayOfWeek === 6) {
          deliveryDate.setUTCDate(deliveryDate.getUTCDate() + 2);
          deliveryStatus = 'EL LUNES';
        } else {
          deliveryDate.setUTCDate(deliveryDate.getUTCDate() + 1);
          deliveryStatus = 'MAÑANA';
        }
      }

      boughtElement.textContent = formatCalendarDate(today);
      receivedElement.textContent = formatCalendarDate(deliveryDate);

      if (!isSunday && !isAfterCutoff) {
        const remainingSeconds = Math.max(
          0,
          dailyCutoffHour * 60 * 60 - (parts.hour * 60 * 60 + parts.minute * 60 + parts.second)
        );
        const remainingTotalMinutes = Math.max(1, Math.ceil(remainingSeconds / 60));
        const remainingHours = Math.floor(remainingTotalMinutes / 60);
        const remainingMinutes = remainingTotalMinutes % 60;
        const timeLabel = `${String(remainingHours).padStart(2, '0')}h ${String(remainingMinutes).padStart(2, '0')}m`;

        statusElement.replaceChildren();
        const timeStrong = document.createElement('b');
        timeStrong.textContent = timeLabel;
        const todayStrong = document.createElement('b');
        todayStrong.textContent = 'HOY';
        statusElement.append(
          document.createTextNode('Haz tu pedido en '),
          timeStrong,
          document.createTextNode(' y recíbelo '),
          todayStrong
        );
      } else {
        replaceStatus(statusElement, 'Te vamos a llevar tu paquete ', deliveryStatus);
      }
    };

    const pause = () => {
      window.clearTimeout(timerId);
      timerId = 0;
    };

    const schedule = () => {
      pause();
      if (document.hidden || !root.isConnected) return;
      update();
      const nextMinuteDelay = 60000 - (Date.now() % 60000) + 50;
      timerId = window.setTimeout(schedule, nextMinuteDelay);
    };

    return {
      start: schedule,
      pause,
      destroy: pause,
    };
  };

  const collectRoots = (scope) => {
    const roots = [];
    if (scope instanceof Element && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    if (typeof scope.querySelectorAll === 'function') roots.push(...scope.querySelectorAll(ROOT_SELECTOR));
    return roots;
  };

  const init = (scope = document) => {
    collectRoots(scope).forEach((root) => {
      if (!controllers.has(root)) controllers.set(root, createController(root));
      controllers.get(root).start();
    });
  };

  const pause = (scope = document) => {
    collectRoots(scope).forEach((root) => controllers.get(root)?.pause());
  };

  const destroy = (scope) => {
    collectRoots(scope).forEach((root) => {
      controllers.get(root)?.destroy();
      controllers.delete(root);
    });
  };

  window[MODULE_KEY] = { init, pause, destroy };

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) pause(document);
    else init(document);
  });
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
  window.addEventListener('pagehide', () => pause(document));
  window.addEventListener('pageshow', () => init(document));

  init(document);
})();
